/*
 * LienzoMoments — subida de la foto del cliente (DT-01, DT-08, DT-11)
 * ---------------------------------------------------------------------------
 * Valida en el navegador antes de que el archivo salga del telefono:
 *
 *   formato    JPG, PNG o WebP. HEIC se rechaza con instrucciones, porque
 *              Safari a veces lo reetiqueta como image/jpeg y solo la
 *              extension delata el archivo real.
 *   peso       por encima del tope se recomprime con canvas. Sin librerias
 *              externas: el proyecto no mete dependencias en la ruta critica
 *              de conversion, y esto son treinta lineas.
 *   proporcion no rechaza (DT-08), pero decide (DT-11). Se mide cuanto de
 *              la foto se pierde al llevarla al formato elegido, en la
 *              orientacion que mejor le queda, y cae en uno de tres
 *              escenarios:
 *
 *              1  recorte <= 15 %: entra sin preguntar.
 *              2  recorte de 15 a 35 %: el cliente elige cambiar al formato
 *                 recomendado o mantener el suyo autorizando el recorte.
 *              3  recorte > 35 %, o la foto no entra con <= 15 % en ningun
 *                 formato: cambiar, si hay a donde, o subir otra foto.
 *                 Mantener no se ofrece. Sin formato al que cambiar, la
 *                 foto queda rechazada y cuenta como no subida.
 *
 *              En 2 y 3 no hay nada preseleccionado y el CTA sigue cerrado
 *              hasta que cada foto con problema tiene su decision. Los
 *              umbrales son ajustes del bloque. Sin la opcion Formato el
 *              unico formato es 3:2 y nunca hay a donde cambiar.
 *
 * Lo que viaja al pedido por cada foto:
 *   properties[Foto N]                        el archivo
 *   properties[Orientación foto N]            vertical, horizontal o cuadrada
 * Y una vez por pedido:
 *   properties[Formato]                       el elegido, si hay formatos
 *   properties[Ajuste de encuadre]            SOLO si el cliente autorizo
 *                                             mantener con recorte
 *
 * CONTRATO CON EL CANDADO DEL CTA (DT-10)
 * Cada ranura con una foto ya validada lleva `data-lm-filled="true"`; la
 * que espera decision lleva ademas `data-lm-pending="true"`, y la que no se
 * puede usar, `data-lm-rejected="true"`. Cada cambio
 * emite `lm:photos-changed`. `lienzomoments-gate.js` es quien decide con
 * eso si el "Agregar al carrito" se abre. Aqui no se toca ningun boton:
 * este archivo sabe de fotos, no de compra.
 */
(() => {
  'use strict';

  const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_EDGE = 4000;

  /* Sin opcion Formato el producto es el de siempre: solo 3:2. */
  const LEGACY_FORMATS = ['3:2'];

  /* Por debajo de esta diferencia una foto se llama cuadrada. */
  const SQUARE_MARGIN = 0.03;

  /*
   * Proporciones con nombre para describir la foto en el pedido ("foto 1 es
   * 4:5"). Si ninguna queda a menos de LABEL_MARGIN, se escribe el decimal.
   */
  const KNOWN_RATIOS = [[1, 1], [5, 4], [4, 3], [3, 2], [16, 9], [2, 1], [21, 9]];
  const LABEL_MARGIN = 0.03;

  const ORDINALS = ['primera', 'segunda', 'tercera', 'cuarta', 'quinta', 'sexta', 'séptima'];

  const MESSAGES = {
    format: 'Sube tu foto en JPG, PNG o WebP.',
    heic: 'Las fotos HEIC de iPhone no se pueden subir directo. Ábrelas en la app Fotos, expórtalas como JPG y súbelas de nuevo.',
    tooBig: 'Tu foto es muy grande y no pudimos reducirla. Prueba con una versión más ligera.',
    unreadable: 'No pudimos leer tu foto. Prueba con otra.',
  };

  /** Lee las dimensiones ya corregidas por la orientación EXIF del archivo. */
  const measure = (file) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => resolve({ img, url, width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('unreadable'));
      };
      img.src = url;
    });

  /* El criterio de encuadre vive en lienzomoments-fit.js, compartido con la
     pagina de servicios sueltos. Ese archivo carga antes que este. */
  const { flat, crop } = window.LMFit;

  const orientationOf = (ratio) => {
    if (Math.abs(ratio - 1) <= SQUARE_MARGIN) return 'cuadrada';
    return ratio < 1 ? 'vertical' : 'horizontal';
  };

  /** "4:5", "1:1", "16:9" o "1.62:1", en la orientacion de la foto. */
  const ratioLabel = (ratio) => {
    const p = flat(ratio);
    let best = null;
    KNOWN_RATIOS.forEach(([a, b]) => {
      const gap = Math.abs(p / (a / b) - 1);
      if (gap <= LABEL_MARGIN && (!best || gap < best.gap)) best = { gap, a, b };
    });
    if (!best) return ratio >= 1 ? `${p.toFixed(2)}:1` : `1:${p.toFixed(2)}`;
    return ratio >= 1 || best.a === best.b ? `${best.a}:${best.b}` : `${best.b}:${best.a}`;
  };

  /** Centavos de Shopify a "$1,599". */
  const money = (cents) => '$' + Math.round(Number(cents) / 100).toLocaleString('es-MX');

  const fill = (template, values) =>
    String(template || '').replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match));

  const readJson = (selector) => {
    const node = document.querySelector(selector);
    if (!node) return {};
    try {
      return JSON.parse(node.textContent) || {};
    } catch {
      return {};
    }
  };

  /** Recomprime con canvas hasta caber en el tope, sin dependencias externas. */
  const compress = async (file, maxBytes) => {
    const { img, url } = await measure(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);

    for (const quality of [0.92, 0.85, 0.75, 0.65]) {
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
      if (blob && blob.size <= maxBytes) {
        return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
      }
    }
    throw new Error('tooBig');
  };

  const init = (root) => {
    if (root.dataset.lmReady === 'true') return;
    root.dataset.lmReady = 'true';

    const maxBytes = (Number(root.dataset.lmMaxMb) || 18) * 1024 * 1024;
    const minor = (Number(root.dataset.lmTolerance) || 15) / 100;
    const drastic = (Number(root.dataset.lmDrastic) || 35) / 100;
    const slots = Array.from(root.querySelectorAll('[data-lm-slot]'));
    const messages = readJson('[data-lm-decision-messages]');

    const formatProp = root.querySelector('[data-lm-format-prop]');
    const adjustProp = root.querySelector('[data-lm-adjust-prop]');
    const switchedNote = root.querySelector('[data-lm-adjust-done]');

    /**
     * Formatos del producto, leidos de los radios del selector, con su
     * nombre comercial. Sin radios, el producto de siempre: solo 3:2 y sin
     * cambio de formato posible.
     */
    const formatInputs = Array.from(document.querySelectorAll('[data-lm-format-input]'));
    const hasFormats = formatInputs.length > 0;
    const formats = hasFormats ? formatInputs.map((input) => input.value) : LEGACY_FORMATS;
    const formatNames = {};
    formatInputs.forEach((input) => {
      const name = input.closest('.lm-format')?.querySelector('.lm-format__name')?.textContent.trim();
      formatNames[input.value] = name && name !== input.value ? `${name} (${input.value})` : input.value;
    });
    const named = (format) => formatNames[format] || format;

    /**
     * Estado por ranura: la proporcion medida y una version que sube con
     * cada archivo nuevo. El escenario se recalcula contra el formato del
     * momento, asi que cambiar de formato o de tamano con la foto puesta no
     * obliga a releer el archivo.
     *
     * La decision se guarda con la clave formato + version: si cambia la
     * foto o el formato elegido, la decision anterior deja de valer y se
     * vuelve a preguntar. Cambiar de tamano dentro del formato no la toca,
     * porque el recorte es el mismo.
     */
    const state = slots.map(() => ({ ratio: null, version: 0 }));
    const decisions = slots.map(() => null);

    let currentSize = null;
    let currentLabels = [];
    let currentCount = 1;
    let currentFormat = null;
    let currentPackage = null;
    let currentPrice = null;
    let switched = null;

    const chosenFormat = () => (hasFormats ? currentFormat : LEGACY_FORMATS[0]);
    const isActive = (index) => index < currentCount;
    const keyFor = (index) => `${chosenFormat()}|${state[index].version}`;

    /**
     * 1, 2 o 3, o null sin foto. 3 tambien cuando la foto no entra con el
     * recorte menor en NINGUN formato (9:16, 21:9): ahi no hay ajuste que la
     * deje bien y mantener no se ofrece.
     */
    const scenarioOf = (index) => {
      const { ratio } = state[index];
      if (!ratio) return null;
      const chosen = crop(ratio, chosenFormat());
      const best = Math.min(...formats.map((format) => crop(ratio, format)));
      if (chosen > drastic || best > minor) return 3;
      if (chosen > minor) return 2;
      return 1;
    };

    /** La decision vigente de una ranura, o null si no hay o ya no aplica. */
    const decisionOf = (index) => {
      const saved = decisions[index];
      if (!saved || saved.key !== keyFor(index)) return null;
      const scenario = scenarioOf(index);
      if (saved.choice === 'keep' && scenario !== 2) return null;
      return saved.choice;
    };

    const isPending = (index) => isActive(index) && (scenarioOf(index) ?? 1) > 1 && !decisionOf(index);

    /**
     * El tamano al que llevaria el cambio: en el formato nuevo y el mismo
     * paquete, el de precio mas cercano al de ahora; en empate, el menor.
     * Null si no hay (los combos solo existen en 3:2).
     */
    const targetFor = (format) => {
      if (!currentPackage || currentPrice == null) return null;
      let best = null;
      document.querySelectorAll(`[data-lm-tier][data-lm-format="${CSS.escape(format)}"]`).forEach((card) => {
        if (card.classList.contains('lm-tier--unavailable')) return;
        const input = card.querySelector('.lm-tier__input');
        if (!input || input.value !== currentPackage) return;
        const price = Number(input.dataset.lmPrice);
        const gap = Math.abs(price - currentPrice);
        if (!best || gap < best.gap || (gap === best.gap && price < best.price)) {
          best = { gap, price, size: card.dataset.lmSize };
        }
      });
      return best;
    };

    /**
     * La opcion de cambiar, para una foto. Solo se ofrece un formato en el
     * que TODAS las fotos ya puestas entran con el recorte menor: cambiar el
     * formato cambia el de todo el paquete, y arreglar una foto descuadrando
     * otra no es arreglar nada. Entre los que cumplen, el que menos recorta
     * esta foto.
     */
    const switchFor = (index) => {
      if (!hasFormats) return null;
      const chosen = chosenFormat();
      const uploaded = state.slice(0, currentCount).filter((s) => s.ratio);
      const candidates = formats
        .filter((format) => format !== chosen)
        .filter((format) => uploaded.every((s) => crop(s.ratio, format) <= minor))
        .sort((a, b) => crop(state[index].ratio, a) - crop(state[index].ratio, b));
      for (const format of candidates) {
        const target = targetFor(format);
        if (target) return { format, ...target };
      }
      return null;
    };

    const parts = (slot) => ({
      input: slot.querySelector('[data-lm-file]'),
      orientation: slot.querySelector('[data-lm-orientation]'),
      preview: slot.querySelector('[data-lm-preview]'),
      thumb: slot.querySelector('[data-lm-thumb]'),
      filename: slot.querySelector('[data-lm-filename]'),
      status: slot.querySelector('[data-lm-status]'),
      error: slot.querySelector('[data-lm-error]'),
      label: slot.querySelector('[data-lm-slot-label]'),
      drop: slot.querySelector('[data-lm-drop]'),
      change: slot.querySelector('[data-lm-change]'),
      decision: slot.querySelector('[data-lm-decision]'),
      replace: slot.querySelector('[data-lm-replace]'),
    });

    /** El tamano que nombra el mensaje: el del combo si lo hay, si no el general. */
    const sizeFor = (index) => currentLabels[index] || currentSize || '';

    /** "Tu foto", "Tu segunda foto", "Tu primera foto (60×90)". */
    const photoName = (index) => {
      if (currentCount === 1) return 'Tu foto';
      const label = currentLabels[index] ? ` (${currentLabels[index]})` : '';
      return `Tu ${ORDINALS[index]} foto${label}`;
    };

    /**
     * Cuenta solo las ranuras que pide la variante de ahora. Al bajar de Trio
     * a Individual las fotos 2 y 3 se conservan escondidas —volver a subir
     * castiga a quien solo estaba comparando precios— pero no cuentan para
     * este pedido, porque no viajan en el submit.
     */
    const filledCount = () => state.slice(0, currentCount).filter((s) => s.ratio).length;

    const announcePhotos = () => {
      document.dispatchEvent(
        new CustomEvent('lm:photos-changed', {
          detail: {
            uploaded: filledCount(),
            required: currentCount,
            pending: slots.filter((slot, index) => isPending(index)).length,
          },
        })
      );
    };

    const showError = (slot, message) => {
      const { error } = parts(slot);
      error.textContent = message;
      error.hidden = false;
    };

    const clearError = (slot) => {
      parts(slot).error.hidden = true;
    };

    /** Las opciones de la decision de una ranura: cuales se ven y que dicen. */
    const renderDecision = (index, scenario, choice) => {
      const { decision } = parts(slots[index]);
      if (!decision) return;

      if (!isActive(index) || !scenario || scenario === 1) {
        decision.hidden = true;
        decision.querySelectorAll('input').forEach((radio) => {
          radio.checked = false;
        });
        return;
      }

      const chosen = chosenFormat();
      const change = switchFor(index);
      const values = {
        photo: photoName(index),
        chosen: named(chosen),
        chosen_ratio: chosen,
        recommended: change ? named(change.format) : '',
        ratio: change ? change.format : '',
        size: change ? change.size : '',
        price: change ? money(change.price) : '',
      };

      const rejected = scenario === 3 && !change;
      decision.querySelector('[data-lm-decision-title]').textContent = fill(
        rejected ? messages.rejected : messages.title,
        values
      );

      const options = {
        switch: change ? [messages.switch, messages.switchDetail] : null,
        keep: scenario === 2 ? [messages.keep, messages.keepConsent] : null,
      };

      // Subir otra foto: siempre en el escenario 3, que es el unico en el
      // que la foto no se puede quedar como esta.
      const { replace } = parts(slots[index]);
      if (replace) replace.hidden = scenario !== 3;

      decision.querySelectorAll('[data-lm-option]').forEach((option) => {
        const kind = option.dataset.lmOption;
        const texts = options[kind];
        const radio = option.querySelector('input');
        option.hidden = !texts;
        radio.disabled = !texts;
        radio.checked = Boolean(texts) && choice === kind;
        if (!texts) return;
        option.querySelector('[data-lm-option-label]').textContent = fill(texts[0], values);
        option.querySelector('[data-lm-option-detail]').textContent = fill(texts[1], values);
      });

      decision.__lm = { change, values };
      decision.hidden = false;
    };

    /** Foto en el escenario 3 sin formato al que cambiar: no se puede usar. */
    const isRejected = (index) => isActive(index) && scenarioOf(index) === 3 && !switchFor(index);

    /** Mensaje, marcas para el candado y decision. */
    const renderSlot = (index) => {
      const slot = slots[index];
      const { status } = parts(slot);
      const scenario = scenarioOf(index);
      const choice = decisionOf(index);

      if (isPending(index)) slot.dataset.lmPending = 'true';
      else delete slot.dataset.lmPending;

      // El candado cuenta la foto rechazada como no subida: el boton pide
      // otra foto, no una decision.
      if (isRejected(index)) slot.dataset.lmRejected = 'true';
      else delete slot.dataset.lmRejected;

      renderDecision(index, scenario, choice);
      if (!scenario) return;

      const values = { size: sizeFor(index), chosen_ratio: chosenFormat() };
      let template = messages.statusReady;
      if (isRejected(index)) template = messages.statusRejected;
      else if (scenario > 1) template = choice === 'keep' ? messages.statusKept : messages.statusPending;
      status.textContent = fill(template, values);
      status.classList.toggle('lm-upload__status--review', scenario > 1);
    };

    /** Propiedades de todo el pedido y la nota de cambio de formato. */
    const renderOrder = () => {
      const chosen = chosenFormat();

      if (formatProp) {
        const on = hasFormats && Boolean(chosen);
        formatProp.disabled = !on;
        formatProp.value = on ? named(chosen) : '';
      }

      if (adjustProp) {
        const kept = state
          .slice(0, currentCount)
          .map((s, index) => (decisionOf(index) === 'keep' ? `foto ${index + 1} es ${ratioLabel(s.ratio)} y va en ${chosen}` : null))
          .filter(Boolean);
        adjustProp.disabled = kept.length === 0;
        adjustProp.value = kept.length ? `Sí, ${kept.join('; ')}, autorizado por el cliente` : '';
      }

      if (switchedNote) {
        const show = Boolean(switched) && switched.format === chosen;
        switchedNote.textContent = show ? switched.text : '';
        switchedNote.hidden = !show;
      }
    };

    const renderAll = () => {
      slots.forEach((slot, index) => renderSlot(index));
      renderOrder();
    };

    /* Las decisiones, por delegacion: los radios viven en cada ranura. */
    root.addEventListener('change', (event) => {
      const radio = event.target;
      if (!radio.matches || !radio.matches('.lm-decision__input') || !radio.checked) return;
      const slot = radio.closest('[data-lm-slot]');
      const index = slots.indexOf(slot);
      if (index < 0) return;

      if (radio.value === 'switch') {
        const { change, values } = parts(slot).decision.__lm || {};
        if (!change) return;
        switched = { format: change.format, text: fill(messages.switched, values) };
        // El selector cambia formato y tamano y conserva el paquete. Su
        // `lm:variant` vuelve aqui y repinta todo contra el formato nuevo.
        document.dispatchEvent(new CustomEvent('lm:select', { detail: { size: change.size, package: currentPackage } }));
        return;
      }

      decisions[index] = { key: keyFor(index), choice: radio.value };
      renderAll();
      announcePhotos();
    });

    const reset = (slot, index) => {
      const { input, orientation, preview, thumb } = parts(slot);
      input.value = '';
      orientation.value = '';
      orientation.disabled = true;
      preview.hidden = true;
      if (thumb.dataset.objectUrl) {
        URL.revokeObjectURL(thumb.dataset.objectUrl);
        delete thumb.dataset.objectUrl;
      }
      thumb.removeAttribute('src');
      delete slot.dataset.lmFilled;
      delete slot.dataset.lmPending;
      delete slot.dataset.lmRejected;
      if (index != null) {
        state[index] = { ratio: null, version: state[index].version + 1 };
        decisions[index] = null;
      }
      switched = null;
    };

    const accept = (slot, index, file, ratio, objectUrl) => {
      const { orientation, preview, thumb, filename } = parts(slot);

      orientation.value = orientationOf(ratio);
      orientation.disabled = false;

      if (thumb.dataset.objectUrl) URL.revokeObjectURL(thumb.dataset.objectUrl);
      thumb.src = objectUrl;
      thumb.dataset.objectUrl = objectUrl;
      filename.textContent = file.name;

      state[index] = { ratio, version: state[index].version + 1 };
      decisions[index] = null;
      switched = null;

      preview.hidden = false;
      clearError(slot);

      // La marca que lee el candado del CTA. Se pone AQUI y no en el `change`
      // del input: aqui el archivo ya paso formato, peso y lectura. Un HEIC
      // rechazado no puede abrir el boton.
      slot.dataset.lmFilled = 'true';
    };

    const process = async (slot, index) => {
      const { input } = parts(slot);
      const file = input.files && input.files[0];
      if (!file) return;

      clearError(slot);

      if (/\.hei[cf]$/i.test(file.name)) {
        reset(slot, index);
        return showError(slot, MESSAGES.heic);
      }
      if (!ACCEPTED.includes(file.type)) {
        reset(slot, index);
        return showError(slot, MESSAGES.format);
      }

      let measured;
      try {
        measured = await measure(file);
      } catch {
        reset(slot, index);
        return showError(slot, MESSAGES.unreadable);
      }

      const ratio = measured.width / measured.height;

      let finalFile = file;
      if (file.size > maxBytes) {
        try {
          finalFile = await compress(file, maxBytes);
          const transfer = new DataTransfer();
          transfer.items.add(finalFile);
          input.files = transfer.files;
        } catch {
          URL.revokeObjectURL(measured.url);
          reset(slot, index);
          return showError(slot, MESSAGES.tooBig);
        }
      }

      accept(slot, index, finalFile, ratio, measured.url);
    };

    /**
     * Envoltorio de `process`. El repintado y el aviso salen en `finally`
     * para que salgan tambien por los caminos de error: un archivo rechazado
     * deja la ranura vacia, y el candado tiene que enterarse igual que si se
     * hubiera aceptado. Se repinta todo y no solo esta ranura: una foto nueva
     * puede cambiar si las demas tienen a donde cambiar de formato.
     */
    const handle = async (slot, index) => {
      try {
        await process(slot, index);
      } finally {
        renderAll();
        announcePhotos();
      }
    };

    slots.forEach((slot, index) => {
      const { input, change, drop, replace } = parts(slot);

      input.addEventListener('change', () => handle(slot, index));
      change.addEventListener('click', () => input.click());

      // Quita la foto y abre el selector de archivos en la misma ranura. Se
      // vacia antes de abrir: si el cliente cancela, la ranura queda vacia y
      // el boton pide la foto, en vez de quedarse con una que no sirve.
      replace?.addEventListener('click', () => {
        reset(slot, index);
        renderAll();
        announcePhotos();
        input.click();
      });

      ['dragenter', 'dragover'].forEach((type) =>
        drop.addEventListener(type, (event) => {
          event.preventDefault();
          drop.classList.add('is-dragging');
        })
      );
      ['dragleave', 'drop'].forEach((type) =>
        drop.addEventListener(type, () => drop.classList.remove('is-dragging'))
      );
      drop.addEventListener('drop', (event) => {
        event.preventDefault();
        const dropped = event.dataTransfer?.files?.[0];
        if (!dropped) return;
        const transfer = new DataTransfer();
        transfer.items.add(dropped);
        input.files = transfer.files;
        handle(slot, index);
      });
    });

    /**
     * Ajusta cuantas ranuras se ven, como se llama la propiedad de cada una y
     * que tamano nombra su mensaje. En los combos multi-tamano el nombre lleva
     * el tamano entre parentesis para que el pedido diga que foto va en que
     * formato: properties[Foto 2 (40×60)].
     */
    const applyVariant = ({
      photoCount = 1,
      photoLabels = [],
      size = null,
      format = null,
      package: pkg = null,
      price = null,
    }) => {
      currentSize = size;
      currentLabels = photoLabels;
      currentCount = photoCount;
      currentFormat = format;
      currentPackage = pkg;
      currentPrice = price == null || Number.isNaN(Number(price)) ? null : Number(price);

      slots.forEach((slot, index) => {
        const position = index + 1;
        const active = position <= photoCount;
        const { input, orientation, label } = parts(slot);
        const sizeLabel = photoLabels[index];

        slot.hidden = !active;
        input.disabled = !active;

        /*
         * La ranura que sobra se esconde, NO se vacia. Antes se llamaba a
         * `reset` aqui y bajar de Trio a Individual borraba dos fotos ya
         * subidas; volver a Trio obligaba a repetir el trabajo. Como el input
         * queda deshabilitado, el archivo sigue en memoria pero no viaja en el
         * submit, que es la unica garantia que hacia falta.
         */
        if (!active) {
          orientation.disabled = true;
          clearError(slot);
          return;
        }

        // La que vuelve a entrar recupera su orientacion sin releer el
        // archivo.
        const saved = state[index];
        if (saved.ratio) {
          orientation.value = orientationOf(saved.ratio);
          orientation.disabled = false;
        }

        const suffix = sizeLabel ? ` (${sizeLabel})` : '';
        input.name = `properties[Foto ${position}${suffix}]`;
        orientation.name = `properties[Orientación foto ${position}${suffix}]`;

        if (photoCount === 1) {
          label.textContent = 'Toca aquí para subir tu foto';
        } else if (sizeLabel) {
          label.textContent = `Foto ${position} — para el cuadro de ${sizeLabel}`;
        } else {
          label.textContent = `Foto ${position} de ${photoCount}`;
        }
      });

      // Mensajes, decisiones y propiedades contra el tamano y el formato
      // nuevos.
      renderAll();
      announcePhotos();
    };

    document.addEventListener('lm:variant', (event) => applyVariant(event.detail ?? {}));

    /* --- Vaciado despues de agregar al carrito ---------------------------- */

    const resetAll = () => {
      slots.forEach((slot, index) => {
        reset(slot, index);
        clearError(slot);
      });
      root.querySelectorAll('[data-lm-service]').forEach((box) => {
        box.checked = false;
      });
      switched = null;
      renderAll();
      announcePhotos();
    };

    /*
     * El pedido ya salio: `product-form.js` construye su `FormData` antes de
     * emitir este evento (assets/product-form.js:420 y :441), asi que vaciar
     * aqui no le quita nada al envio en curso.
     *
     * Se espera a la promesa del evento porque el aviso es optimista: se
     * dispara antes de que el fetch conteste. Si el alta fallo (`didError`),
     * las fotos se quedan donde estan y el cliente puede reintentar sin
     * volver a subirlas.
     */
    const onCartUpdate = (event) => {
      if (event.action && event.action !== 'add') return;

      const done = (result) => {
        if (result?.detail?.didError) return;
        resetAll();
      };

      if (event.promise && typeof event.promise.then === 'function') {
        event.promise.then(done, () => {});
      } else {
        done();
      }
    };

    /*
     * El nombre literal sale de `assets/standard-actions.d.ts:56`. Se pregunta
     * ademas al modulo de eventos por si Shopify lo renombra, pero el literal
     * va primero y sin await: si el import fallara, el vaciado seguiria
     * funcionando.
     */
    document.addEventListener('shopify:cart:lines-update', onCartUpdate);

    import('@shopify/events')
      .then((module) => {
        const name = module?.StandardEvents?.cartLinesUpdate;
        if (name && name !== 'shopify:cart:lines-update') {
          document.addEventListener(name, onCartUpdate);
        }
      })
      .catch(() => {});

    /*
     * Estado inicial leido del DOM, no del evento.
     *
     * Los dos scripts van con `defer`, asi que se ejecutan en orden de
     * aparicion: el selector primero, este despues. El selector emite su
     * `lm:variant` inicial durante su propia carga, cuando este listener
     * todavia no existe. Si dependieramos solo del evento, un paquete de
     * varias fotos elegido por defecto arrancaria con una sola ranura.
     */
    const checked = document.querySelector('[data-lm-tier] .lm-tier__input:checked');
    if (checked) {
      const labels = checked.dataset.lmPhotoLabels;
      const card = checked.closest('[data-lm-tier]');
      applyVariant({
        photoCount: Number(checked.dataset.lmPhotos) || 1,
        photoLabels: labels ? labels.split('|') : [],
        size: card?.dataset.lmSize ?? null,
        format: card?.dataset.lmFormat ?? null,
        package: checked.value,
        price: checked.dataset.lmPrice,
      });
    }
  };

  const boot = () => document.querySelectorAll('[data-lm-upload]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
